import { AdminAccountState, OFFICE_ROLES, PRICING_PER_TRANSACTION, PRICING_PER_USER } from 'Interfaces/Admin';

/**
 *  Licence seat rules, gathered from the four places that enforced them:
 *  UserView.maxUsersReached, UserView.deactivateUser, UserView.roleChanged and
 *  DetailsView's inline grid `updaterow`.
 *
 *  Those four did not agree, and the differences are load-bearing rather than
 *  cosmetic, so they are kept as distinct named checks instead of being merged:
 *
 *    - Creating a user allows `maxUsers + monthlyLicences` seats; activating an
 *      existing one allows only `maxUsers`.
 *    - When creating, a task assignee is a field seat; when activating or
 *      changing role, a task assignee counts against office seats.
 *
 *  A limit of 0 means unlimited, which is why every check is guarded on
 *  `limit > 0` exactly as the originals were.
 */

export type SeatDenial = 'office' | 'active';

export interface SeatCheck {
  allowed: boolean;
  /** Which pool ran out; undefined when allowed. */
  denied?: SeatDenial;
}

const ALLOWED: SeatCheck = { allowed: true };

/** Roles that do not consume an office seat when a user is being created. */
export const FIELD_ROLES_ON_CREATE = ['enumerator', 'viewer', 'taskAssignee'];

/** Roles that do not consume an office seat when a user is being activated. */
export const FIELD_ROLES_ON_ACTIVATE = ['enumerator', 'viewer'];

export type SeatContext = Pick<
AdminAccountState, 'pricingPlan' | 'maxUsers' | 'monthlyLicences' | 'activeUsers' | 'officeUsers'
>;

/**
 *  Can a brand new user be created as active with this role?
 *  Mirrors UserView.maxUsersReached — note the monthly licences top-up.
 */
export const canAddActiveUser = (ctx: SeatContext, role: string): SeatCheck => {
  const limit = Number(ctx.maxUsers) + Number(ctx.monthlyLicences);
  if (limit <= 0) {
    return ALLOWED;
  }
  if (ctx.pricingPlan === PRICING_PER_TRANSACTION) {
    if (FIELD_ROLES_ON_CREATE.includes(role)) {
      return ALLOWED;
    }
    return ctx.officeUsers >= limit ? { allowed: false, denied: 'office' } : ALLOWED;
  }
  if (ctx.pricingPlan === PRICING_PER_USER && role !== 'viewer') {
    return ctx.activeUsers >= limit ? { allowed: false, denied: 'active' } : ALLOWED;
  }
  return ALLOWED;
};

/**
 *  Can an existing, currently inactive user be switched back on?
 *  Mirrors UserView.deactivateUser and DetailsView's `updaterow`, which both
 *  compare against `maxUsers` alone.
 */
export const canActivateUser = (ctx: SeatContext, role: string): SeatCheck => {
  const limit = Number(ctx.maxUsers);
  if (limit <= 0) {
    return ALLOWED;
  }
  if (ctx.pricingPlan === PRICING_PER_TRANSACTION) {
    if (FIELD_ROLES_ON_ACTIVATE.includes(role)) {
      return ALLOWED;
    }
    return ctx.officeUsers >= limit ? { allowed: false, denied: 'office' } : ALLOWED;
  }
  if (ctx.pricingPlan === PRICING_PER_USER && role !== 'viewer') {
    return ctx.activeUsers >= limit ? { allowed: false, denied: 'active' } : ALLOWED;
  }
  return ALLOWED;
};

/**
 *  Can an active user be moved onto an office role? Mirrors
 *  UserView.roleChanged, which only guards the Per Transaction plan.
 */
export const canAssignOfficeRole = (ctx: SeatContext, role: string): SeatCheck => {
  if (ctx.pricingPlan !== PRICING_PER_TRANSACTION) {
    return ALLOWED;
  }
  if (!OFFICE_ROLES.includes(role) && role !== 'admin') {
    return ALLOWED;
  }
  const limit = Number(ctx.maxUsers);
  if (limit > 0 && ctx.officeUsers >= limit) {
    return { allowed: false, denied: 'office' };
  }
  return ALLOWED;
};

/** True when the role occupies an office seat rather than a field seat. */
export const isOfficeRole = (role: string): boolean => OFFICE_ROLES.includes(role);

/**
 *  How the seat counters move when a user's active flag flips, matching the
 *  optimistic `#activeUsers` / `#officeUsers` updates UserSync used to make.
 */
export const seatDelta = (role: string, activating: boolean): { active: number; office: number } => {
  const step = activating ? 1 : -1;
  return {
    active: step,
    office: isOfficeRole(role) ? step : 0,
  };
};
